import { useEffect, useState } from "react";
import { MagnifyingGlass, CircleNotch, DeviceMobile, Desktop } from "@phosphor-icons/react";
import { COUNTRIES } from "@/lib/countries";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export const SearchHeader = ({ onSubmit, loading, initial }) => {
    const [businessType, setBusinessType] = useState(initial?.business_type || "");
    const [country, setCountry] = useState(initial?.country || "au");
    const [city, setCity] = useState(initial?.city || "");
    const [device, setDevice] = useState(initial?.device || "mobile");
    const [competitorUrls, setCompetitorUrls] = useState("");
    const [showCompetitors, setShowCompetitors] = useState(false);

    // Keep the device selector in sync when a history record is loaded.
    useEffect(() => {
        setDevice(initial?.device || "mobile");
    }, [initial?.device]);

    const handle = (e) => {
        e.preventDefault();
        if (!businessType.trim() || !city.trim()) return;
        const urls = competitorUrls
            .split(/\r?\n/)
            .map((u) => u.trim())
            .filter(Boolean)
            .slice(0, 3);
        onSubmit({
            business_type: businessType.trim(),
            country,
            city: city.trim(),
            competitor_urls: urls,
            device,
        });
    };

    return (
        <header
            className="sticky top-0 z-40 border-b border-neutral-200 bg-white"
            data-testid="search-header"
        >
            <div className="px-6 lg:px-10 py-5">
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="flex items-baseline gap-3">
                        <h1 className="font-display text-2xl lg:text-3xl font-black tracking-tighter text-ink">
                            SERP·SCOUT
                        </h1>
                        <span className="mono-label hidden sm:inline">
                            competitor intel · google ads
                        </span>
                    </div>
                    <div className="mono-label hidden md:block">
                        v1.0 / internal
                    </div>
                </div>

                <form
                    onSubmit={handle}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
                >
                    <div className="md:col-span-5">
                        <label className="mono-label block mb-1.5">Business Type</label>
                        <input
                            data-testid="input-business-type"
                            type="text"
                            value={businessType}
                            onChange={(e) => setBusinessType(e.target.value)}
                            placeholder="e.g. hob repair, dentist, plumber"
                            className="w-full h-12 px-3 border border-neutral-300 bg-white text-base focus:border-klein focus:ring-0 focus-visible:outline-2 focus-visible:outline-klein transition-colors duration-150"
                            required
                        />
                    </div>

                    <div className="md:col-span-3">
                        <label className="mono-label block mb-1.5">Country</label>
                        <Select value={country} onValueChange={setCountry}>
                            <SelectTrigger
                                data-testid="select-country"
                                className="w-full h-12 rounded-none border-neutral-300 focus:border-klein focus:ring-0 text-base"
                            >
                                <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="rounded-none border-neutral-300">
                                {COUNTRIES.map((c) => (
                                    <SelectItem
                                        key={c.code}
                                        value={c.code}
                                        data-testid={`country-option-${c.code}`}
                                        className="rounded-none"
                                    >
                                        {c.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="mono-label block mb-1.5">City / Suburb</label>
                        <input
                            data-testid="input-city"
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder="Bondi, Bangalore..."
                            className="w-full h-12 px-3 border border-neutral-300 bg-white text-base focus:border-klein focus:ring-0 focus-visible:outline-2 focus-visible:outline-klein transition-colors duration-150"
                            required
                        />
                    </div>

                    <div className="md:col-span-2 flex gap-1.5">
                        <button
                            data-testid="search-button"
                            type="submit"
                            disabled={loading}
                            className="brutal-btn flex-1 h-12 bg-klein text-white font-mono uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed border border-klein"
                        >
                            {loading ? (
                                <>
                                    <CircleNotch size={16} weight="bold" className="animate-spin" />
                                    Scanning
                                </>
                            ) : (
                                <>
                                    <MagnifyingGlass size={16} weight="bold" />
                                    Search
                                </>
                            )}
                        </button>
                        <Select value={device} onValueChange={setDevice}>
                            <SelectTrigger
                                data-testid="select-device"
                                title="SERP device — Mobile is the default"
                                className="w-[104px] shrink-0 h-12 rounded-none border-neutral-300 focus:border-klein focus:ring-0 text-[11px] font-mono uppercase tracking-wider"
                            >
                                <SelectValue placeholder="Mobile" />
                            </SelectTrigger>
                            <SelectContent className="rounded-none border-neutral-300">
                                <SelectItem
                                    value="mobile"
                                    data-testid="device-option-mobile"
                                    className="rounded-none"
                                >
                                    <span className="flex items-center gap-2">
                                        <DeviceMobile size={13} weight="bold" /> Mobile
                                    </span>
                                </SelectItem>
                                <SelectItem
                                    value="desktop"
                                    data-testid="device-option-desktop"
                                    className="rounded-none"
                                >
                                    <span className="flex items-center gap-2">
                                        <Desktop size={13} weight="bold" /> Desktop
                                    </span>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </form>

                <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1">
                    <button
                        type="button"
                        data-testid="toggle-competitor-urls"
                        onClick={() => setShowCompetitors((v) => !v)}
                        className="mono-label inline-flex items-center gap-1.5 text-neutral-600 hover:text-ink transition"
                    >
                        <span className={`inline-block transition-transform ${showCompetitors ? "rotate-90" : ""}`}>▸</span>
                        Competitor URLs {competitorUrls.trim() ? `(${competitorUrls.split(/\r?\n/).filter((u) => u.trim()).length}/3)` : "(optional)"}
                    </button>
                    <span
                        data-testid="device-status-line"
                        className="mono-label inline-flex items-center gap-1.5 text-neutral-500"
                    >
                        {device === "desktop" ? (
                            <Desktop size={12} weight="bold" className="text-klein" />
                        ) : (
                            <DeviceMobile size={12} weight="bold" className="text-klein" />
                        )}
                        Displaying live {device === "desktop" ? "Desktop" : "Mobile"} Search results.
                    </span>
                </div>
                {showCompetitors && (
                    <div className="mt-2">
                        <textarea
                            data-testid="input-competitor-urls"
                            value={competitorUrls}
                            onChange={(e) => setCompetitorUrls(e.target.value)}
                            placeholder={"Up to 3 URLs, one per line — Claude will extract their keywords & positioning\nhttps://competitor1.com\nhttps://competitor2.com/services\nhttps://competitor3.com"}
                            rows={3}
                            className="w-full px-3 py-2 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm font-mono resize-none"
                        />
                        <p className="mono-label text-neutral-500 mt-1">
                            Adds ~5-10s per URL. Fills the &ldquo;Competitor Comparison&rdquo; panel.
                        </p>
                    </div>
                )}
            </div>

            {loading && (
                <div
                    data-testid="progress-bar"
                    className="h-[3px] w-full overflow-hidden bg-neutral-100"
                >
                    <div className="h-full w-1/3 bg-klein animate-bar-slide"></div>
                </div>
            )}
        </header>
    );
};
